import { makeStyles } from '@material-ui/styles'
import { useCallback, useEffect, useState } from 'react'
import CodeBlock from 'core/components/CodeBlock'
import Button from 'core/elements/button/Button'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import {
  getFailureReasonExplanation,
  splitFailureMessage,
} from 'k8s/components/kaapi/cluster-addons/etcd-backup'

interface AddonErrorModalProps {
  addonName: string
  reason?: string
  message: string
  onClose: () => void
}

// AEM-FR-001: the error details of a failed add-on, like MachineErrorModal for machines
export default function AddonErrorModal({
  addonName,
  reason,
  message,
  onClose,
}: AddonErrorModalProps) {
  const classes = useStyles()
  const [copied, setCopied] = useState(false)
  const explanation = getFailureReasonExplanation(reason)
  const lines = splitFailureMessage(message)

  // AEM-FR-003: Esc closes the modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // AEM-FR-002: copy the reason and the message
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText([reason, ...lines].filter(Boolean).join('\n'))
    setCopied(true)
  }, [reason, lines])

  return (
    <Modal
      title={`Error Details - ${addonName}`}
      onClose={onClose}
      maxWidth={1000}
      panel="dialog"
      footer={
        <>
          <Button variant="secondary" onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
      open
    >
      {!!reason && (
        <div className={classes.reason} data-testid="addon-error-reason">
          <Text variant="caption1">{reason}</Text>
          {explanation && <Text variant="body2">{explanation}</Text>}
        </div>
      )}
      <CodeBlock className={classes.errorBlock}>
        {lines.map((line) => (
          <div key={line} data-testid="addon-error-line">
            {line}
          </div>
        ))}
      </CodeBlock>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  reason: {
    display: 'grid',
    gridGap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  errorBlock: {
    color: theme.components.alert.error.border,
    backgroundColor: theme.components.alert.error.background,
    maxHeight: 320,
    overflow: 'auto',
    display: 'grid',
    gridGap: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}))
